<template>
  <div class="group my-1 w-full cursor-pointer text-sm text-gray-600">
    <loader :is-loading="loading || updateNoteLoading" />
    <div class="flex space-x-3">
      <note-type-icon :note="note" />
      <div class="note-content w-full rounded-sm bg-white shadow-main">
        <slot name="header" />
        <div class="flex justify-between px-6 pt-4">
          <div class="flex w-full items-center justify-between space-x-2">
            <div>
              <span
                v-if="showStatusDescription"
                v-tooltip="{
                  content: statusDescription,
                  placement: 'left',
                  onShow: () => statusDescription.length > 30 && !activePiPDeal,
                }"
                class="max-w-3/4 overflow-hidden rounded-full px-2-5 py-1 text-xxs text-ellipsis whitespace-nowrap uppercase"
                :style="getPillStyle({ color: COLORS[colorCode] })"
              >
                {{ statusDescription }}
              </span>
            </div>
            <action-menu-wrapper
              v-if="menuItems.length"
              :items="menuItems"
              class="action-menu"
              :teleport="
                ![DealActivityState.Draggable, DealActivityState.PiP].includes(
                  dealActivityOptions.state
                )
              "
              :offset="offset"
            />
          </div>
        </div>
        <div v-if="isEditing" class="p-4">
          <text-area
            v-model="noteText"
            class="w-full overflow-y-hidden rounded-md"
            :placeholder="$t('COMMON.DESCRIPTION')"
            name="noteText"
          />
          <div class="flex w-full justify-between space-y-1 bg-white">
            <div class="flex-row-full-justify-between wrapper">
              <notes-dropdown
                v-if="note.notable_type === FullNotableType.call"
                v-model="callDisposition"
                :options="callStatuses"
                :colors-for-options="COLORS_BY_DISPOSITION_NOTE_STATUS"
                :placeholder="$t('ACTIVITY.SELECT_CALL_DISPOSITION')"
                class="max-w-60 min-w-50"
              />
              <modal-buttons
                v-if="isEditing"
                class="ml-auto bg-white"
                :on-cancel="handleCancel"
                :on-save="updateNote"
                no-vertical-padding
              />
            </div>
          </div>
        </div>
        <template v-else>
          <div
            class="note-body break-long-words w-full px-6 pt-3 pb-4 text-gray-700"
            v-html="autoLink(note.comment)"
          />

          <div
            class="note-footer flex items-center border-t px-6 py-2 text-xs text-gray-400"
          >
            <avatar-with-name v-if="note.user" :user="note.user" />

            <span v-if="note.created_at" class="separator px-1">•</span>
            <span v-if="note.created_at" class="updated-date">
              {{ formatDate(note.created_at) }}
            </span>
            <div class="flex items-center space-x-2">
              <seen-note-button
                :seen-by="note.read_notes || []"
                class="seen-note-button ml-0.5"
                data-testid="seen-notes-button"
                :teleport="
                  ![
                    DealActivityState.Draggable,
                    DealActivityState.PiP,
                  ].includes(dealActivityOptions.state)
                "
              />
              <icon-base
                v-tooltip="{
                  content: tooltipText,
                  onShow: () => !activePiPDeal,
                }"
                height="12"
                width="12"
                view-box="0 0 16 16"
                :icon="buttonIcon"
                icon-color="gray"
                class="-mt-2"
                :data-testid="buttonIcon"
              />

              <icon-base
                v-if="!isApplicationNote && showVisibilityIcon"
                height="20"
                width="20"
                view-box="0 0 20 14"
                icon="show-pass-no-bg"
                class="-mt-1 text-gray-500"
                data-testid="visibility-icon"
              />
              <icon-base
                v-if="isApplicationNote && note.visible_in_deal_list"
                v-tooltip="{
                  content: $t('DEALS.DEAL_NOTES.VISIBLE_IN_DEALS_LIST'),
                  onShow: () => !activePiPDeal,
                }"
                data-cy="visible-in-deals-list"
                :icon="IconListView"
                view-box="0 0 24 24"
                class="-mt-1-5 text-gray-500"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { DealActivityState, NotableType } from "@/enums/dealActivity";
import { getPillStyle } from "@/helpers/UI";
import { getEnumKeyFromValue } from "@/helpers/common";
import {
  ALL_NOTE_VISIBILITY_TYPES,
  COLORS,
  NOTE_VISIBILITY_SCOPE,
  NOTE_VISIBILITY_SCOPES,
  STATUS_COLOR_CODES,
} from "@/helpers/constants";
import { DEFAULT_DEAL_ACTIVITY_OPTIONS } from "@/helpers/constants/deals";
import { WORKFLOW_TO_APP_STATUS } from "@/helpers/constants/workflow";
import { autoLink } from "@/helpers/elements";
import { useAuth } from "@/hooks/auth";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { usePromiseWrapper } from "@/hooks/common";
import { useDealsBase } from "@/hooks/deals";
import { useLocalStorageSetting } from "@/hooks/options";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import notesService from "@/services/modules/notes";
import useApplicationsStore from "@/stores/applications";
import useNotesStore from "@/stores/notes";
import usePiPStore from "@/stores/pip";

import type { ApplicationStatus } from "@/models/applications";
import type { primaryVisibilityScope } from "@/models/common";
import type { Note as NoteItem, NotePayload } from "@/models/notes";
import type { IFlowRecord, IWorkflowStage } from "@/models/workflows";
</script>

<script setup lang="ts">
import isEmpty from "lodash/isEmpty";
import { storeToRefs } from "pinia";

import { FullNotableType, NoteType } from "@/enums/dealActivity";
import { COLORS_BY_DISPOSITION_NOTE_STATUS } from "@/helpers/constants/colors";
import { formatDate } from "@/helpers/formatting";

import IconListView from "@/components/icons/IconListView.vue";
import IconPeople from "@/components/icons/IconPeople.vue";
import IconStar from "@/components/icons/IconStar.vue";
import IconUser from "@/components/icons/IconUser.vue";
import ActionMenuWrapper, {
  type MenuItem,
} from "@/components/ui/ActionMenuWrapperV2.vue";
import AvatarWithName from "@/components/ui/AvatarWithName.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import NotesDropdown from "@/components/ui/NotesDropdown.vue";
import TextArea from "@/views/deals/components/dealNotes/AutoresizingTextArea.vue";
import NoteTypeIcon from "@/views/deals/components/dealNotes/NoteTypeIcon.vue";
import SeenNoteButton from "@/views/deals/components/dealNotes/SeenNoteButton.vue";

import type { Component } from "vue";

const iconMap: Record<string, Record<string, Component>> = {
  people: IconPeople,
  user: IconUser,
  star: IconStar,
};

const { note, applicationId } = defineProps<{
  note: NoteItem;
  applicationId?: string;
}>();

const emit = defineEmits<{
  "note:deleted": [];
}>();

const offset = { left: -73, top: 43 };

const { getters } = useStore();
const { isSuperAdmin, isAdmin } = useAuth();
const { activeDeal } = useDealsBase();
const route = useRoute();
const { t } = useI18n();
const applicationsStore = useApplicationsStore();
const copyToClipboard = useClipboardWithMessage();
const dealActivityOptions = useLocalStorageSetting(
  "dealActivityOptions",
  DEFAULT_DEAL_ACTIVITY_OPTIONS
);
const { activePiPDeal, activePiPFlows, activePiPStages } =
  storeToRefs(usePiPStore());
const { canSeeDealActivity } = storeToRefs(applicationsStore);
const notesStore = useNotesStore();

const loading = ref(false);
const isEditing = ref(false);
const noteText = ref(note.comment);
const callDisposition = ref(
  note.call_disposition?.replace(/_/g, " ") ??
    t("ACTIVITY.SELECT_CALL_DISPOSITION")
);

const VISIBILITY_SCOPES_WITH_BUTTONS = [1, 3];
const OTHER_VISIBILITY_SCOPE_INDEX = 99;

const flows = computed<IFlowRecord[]>(() =>
  isEmpty(activePiPFlows.value)
    ? getters["workflows/activeTemplateFlow"]
    : activePiPFlows.value
);

const stages = computed<IWorkflowStage[]>(() =>
  isEmpty(activePiPStages.value)
    ? getters["workflows/activeTemplateStages"]
    : activePiPStages.value
);

const callDispositions = computed<Record<string, string>>(
  () => getters["options/callDispositions"]
);

const callStatuses = computed(() =>
  Object.entries(callDispositions.value).map(([id, value]) => ({
    id,
    name: value,
  }))
);

const isApplicationNote = computed(() => noteType.value === "application");

const isCurrentUserNote = computed(
  () => getters["auth/user"]?.id === note.user?.id
);

const noteFlow = computed(() =>
  flows.value.find((flow) => flow.id === note.stage)
);
const noteStage = computed(() =>
  stages.value.find((stage) => stage.id === noteFlow.value?.stage_id)
);
const noteType = computed(() => NotableType[note.notable_type]);

const colorCode = computed(() => {
  if (noteType.value === "call") {
    return 0;
  }
  if (noteType.value === "business") {
    return 1;
  }
  const stageType = noteStage.value?.type;
  if (stageType === undefined) {
    return 0;
  }
  const statusCode =
    WORKFLOW_TO_APP_STATUS[stageType as keyof typeof WORKFLOW_TO_APP_STATUS];
  return STATUS_COLOR_CODES[statusCode as ApplicationStatus];
});

const statusDescription = computed(() => {
  if (noteType.value === "application") {
    return noteFlow.value?.name || "";
  }
  if (noteType.value === "business") {
    return t("DEALS.NOTES.BUSINESS_NOTE");
  }
  if (noteType.value === "call" && note.call_disposition) {
    return callDispositions.value[note.call_disposition] || "";
  }
  return "";
});

const menuItems = computed<MenuItem[]>(() => {
  const items = [
    isCurrentUserNote.value || (isAdmin && noteType.value === "business")
      ? {
          label: t("COMMON.EDIT"),
          handler: editNote,
        }
      : null,
    note.id
      ? {
          label: t("COMMON.COPY_LINK"),
          handler: () => copyNoteLink(note.id as number),
        }
      : null,
    isCurrentUserNote.value ||
    isSuperAdmin ||
    (isAdmin && noteType.value === "business")
      ? {
          label: t("COMMON.DELETE"),
          handler: deleteNote,
        }
      : null,
  ];

  if (isEditing.value) {
    items.shift();
    items.unshift({
      label: t("COMMON.CANCEL"),
      handler: handleCancel,
    });
  }

  return items.filter(Boolean) as MenuItem[];
});

const showVisibilityIcon = computed(
  () =>
    canSeeDealActivity.value &&
    note.visibility_scope !== NOTE_VISIBILITY_SCOPE.ALL
);

const visibilityIconIndx = computed<primaryVisibilityScope>(() => {
  if (VISIBILITY_SCOPES_WITH_BUTTONS.includes(note.visibility_scope)) {
    return note.visibility_scope as primaryVisibilityScope;
  }
  return OTHER_VISIBILITY_SCOPE_INDEX;
});

const buttonIcon = computed(
  () => iconMap[NOTE_VISIBILITY_SCOPES[visibilityIconIndx.value].icon]
);

const tooltipText = computed(
  () => `visibility: ${ALL_NOTE_VISIBILITY_TYPES[note.visibility_scope]}`
);

const showStatusDescription = computed(() => !!statusDescription.value);

const deleteNote = async () => {
  loading.value = true;
  await notesService.deleteNote(note.id);
  loading.value = false;
  emit("note:deleted");
};

const copyNoteLink = (id: number) => {
  const origin = location?.origin || "";
  const dealId = activePiPDeal.value
    ? activePiPDeal.value?.id
    : (activeDeal.value?.id ?? applicationId);
  const path =
    route.name === ROUTE_DEAL_PROGRESS
      ? route.path
      : `/deals/${dealId}/progress`;
  copyToClipboard(`${origin}${path}?note=${id}`, {
    isPiP: !!activePiPDeal.value,
  });
};

const { fetchWrapper: updateNote, loading: updateNoteLoading } =
  usePromiseWrapper(async () => {
    const payload: NotePayload = {
      id: note.id,
      notable_type: NotableType[note.notable_type],
      notable_id: note.notable_id,
      comment: noteText.value,
      visibility_scope: note.visibility_scope,
    };

    if (
      note.notable_type === getEnumKeyFromValue(NotableType, NoteType.Call) &&
      note.call_disposition
    ) {
      payload.call_disposition = note.call_disposition;
    }
    if (callDisposition.value !== t("ACTIVITY.SELECT_CALL_DISPOSITION")) {
      payload.call_disposition = callDisposition.value;
    }

    await notesStore.updateNote(payload);
    isEditing.value = false;
  });

const handleCancel = () => {
  isEditing.value = false;
  noteText.value = note.comment;
};

const editNote = async () => {
  isEditing.value = true;
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.note-body :deep() {
  a {
    @apply text-primary underline;
  }
}
.note-content {
  container-type: inline-size;
}

.highlight .note-content {
  @apply border border-primary bg-surface-user-blue;
}

@container (max-width: 300px) {
  .note-footer {
    @apply flex-col flex-wrap items-start space-y-1;
    .space-x-2 {
      @apply shrink space-x-3;
    }
    .seen-note-button {
      @apply ml-0;
    }
  }
  .note-footer .separator {
    display: none;
  }
}

:deep() {
  .dropdown {
    @apply top-full right-0 left-auto;
  }
}
</style>
